/**
 * Where to put the page scroller after a client-side navigation.
 *
 * Forward navigations open at the top (or on an in-page anchor). Back and
 * forward restore the offset saved for that history entry. Query edits that
 * only open a sheet (`meet`) or follow the active swimmer (`swimmer`) leave
 * the underlying page where it is. The parent app's home-screen handoff
 * (`a2hs`) is the same kind of overlay: it rewrites the current entry and
 * must not send Back to the top.
 */

export type RouteScrollAction = 'POP' | 'PUSH' | 'REPLACE';

export type RouteScrollLocation = {
  pathname: string;
  search: string;
  hash: string;
  key: string;
  action: RouteScrollAction;
};

export type RouteScrollIntent = 'top' | 'restore' | 'anchor' | 'keep';

/**
 * Query keys that change an overlay on the current page, not the page itself.
 * `a2hs` is the parent app's Add to Home Screen handoff. It is written onto
 * the current history entry with replaceState after the reader may already
 * have scrolled.
 */
export const ROUTE_SCROLL_OVERLAY_QUERY_KEYS = ['meet', 'swimmer', 'a2hs'] as const;

/** Drop overlay params that must not split one history entry into two scroll memories. */
export function routeScrollMemorySearch(search: string): string {
  const params = searchParams(search);
  params.delete('a2hs');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function routeScrollHasHash(hash: string): boolean {
  return hash.length > 1;
}

function searchParams(search: string): URLSearchParams {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

function queryValues(params: URLSearchParams, key: string): string {
  return params.getAll(key).join('\0');
}

/** True when every changed query key is an overlay key (sheet or swimmer). */
export function onlyOverlayQueryChanged(prevSearch: string, nextSearch: string): boolean {
  const prev = searchParams(prevSearch);
  const next = searchParams(nextSearch);
  const keys = new Set<string>([...prev.keys(), ...next.keys()]);
  let changed = false;
  for (const key of keys) {
    if (queryValues(prev, key) === queryValues(next, key)) continue;
    changed = true;
    if (!(ROUTE_SCROLL_OVERLAY_QUERY_KEYS as readonly string[]).includes(key)) return false;
  }
  return changed;
}

export function routeScrollIntent(
  prev: Pick<RouteScrollLocation, 'pathname' | 'search' | 'hash'> | null,
  next: RouteScrollLocation,
): RouteScrollIntent {
  if (!prev) {
    if (next.action === 'POP') return 'restore';
    if (routeScrollHasHash(next.hash)) return 'anchor';
    return 'top';
  }

  const pathChanged = prev.pathname !== next.pathname;
  const searchChanged = prev.search !== next.search;
  const hashChanged = prev.hash !== next.hash;

  if (!pathChanged && searchChanged) {
    if (onlyOverlayQueryChanged(prev.search, next.search)) return 'keep';
    if (next.action === 'POP') return 'restore';
    return 'top';
  }

  if (!pathChanged) {
    // Native in-page anchors (`<a href="#squads">`) arrive as POP because the
    // browser, not React Router, pushed the history entry.
    if (hashChanged && routeScrollHasHash(next.hash)) return 'anchor';
    if (next.action === 'POP') return 'restore';
    return 'keep';
  }

  if (next.action === 'POP') return 'restore';
  if (routeScrollHasHash(next.hash)) return 'anchor';
  return 'top';
}

export type RouteScrollBox = {
  scrollTop: number;
};

/**
 * Apply an intent to one scroller.
 * `tryAnchor` returns true when it scrolled the target into view.
 * Restore with nothing saved falls back to the anchor, then the top.
 */
export function applyRouteScroll(input: {
  intent: RouteScrollIntent;
  savedTop: number | undefined;
  hasHash: boolean;
  box: RouteScrollBox;
  tryAnchor: () => boolean;
}): void {
  const { intent, box } = input;
  if (intent === 'keep') return;
  if (intent === 'anchor') {
    if (!input.tryAnchor()) box.scrollTop = 0;
    return;
  }
  if (intent === 'restore') {
    if (input.savedTop !== undefined) {
      box.scrollTop = input.savedTop;
      return;
    }
    if (input.hasHash && input.tryAnchor()) return;
    box.scrollTop = 0;
    return;
  }
  box.scrollTop = 0;
}

/**
 * Memory id for one history entry. Native in-page anchors often reuse React
 * Router's history key, so the hash and the history index keep the anchor
 * from overwriting the page the reader came from.
 */
export function routeScrollMemoryId(input: {
  historyKey?: string | null;
  historyIdx?: number | null;
  pathname: string;
  search: string;
  hash: string;
}): string {
  const key = input.historyKey || 'default';
  const idx = typeof input.historyIdx === 'number' ? String(input.historyIdx) : 'x';
  return `${key}:${idx}:${input.pathname}${input.search}${input.hash}`;
}

const memory = new Map<string, number>();
const MEMORY_LIMIT = 200;

function memoryKey(scope: string, key: string): string {
  return `${scope}:${key}`;
}

export function rememberRouteScroll(scope: string, key: string, top: number): void {
  const id = memoryKey(scope, key);
  if (memory.has(id)) memory.delete(id);
  memory.set(id, top);
  while (memory.size > MEMORY_LIMIT) {
    const oldest = memory.keys().next().value;
    if (oldest === undefined) break;
    memory.delete(oldest);
  }
}

export function readRouteScroll(scope: string, key: string): number | undefined {
  return memory.get(memoryKey(scope, key));
}

/** Test helper. Production navigation keeps the map for the life of the tab. */
export function resetRouteScrollMemory(): void {
  memory.clear();
}
